

import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';

interface TestimonialData {
  quote: string;
  author: string;
  position: string;
  company: string;
  rating: number;
}

const Testimonials: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials: TestimonialData[] = [
    {
      quote: "ChainCloud云超算平台的IO500性能表现令人印象深刻。我们的分子动力学模拟任务效率提升了60%，极大地加速了我们的研发进程。",
      author: "李教授",
      position: "生物信息学研究所 首席科学家",
      company: "知名科研院所",
      rating: 5
    },
    {
      quote: "作为一家初创公司，我们很难承担昂贵的本地高性能计算设备。ChainCloud云超算让我们以较低的成本获得了企业级的计算能力。",
      author: "王总",
      position: "生物技术公司 CEO",
      company: "某生物科技公司",
      rating: 5
    },
    {
      quote: "从传统的本地计算迁移到云端，我们最担心的是数据安全和性能。ChainCloud云超算完全消除了我们的顾虑，性能甚至超出了预期。",
      author: "陈工程师",
      position: "汽车制造公司 仿真部门负责人",
      company: "某汽车制造商",
      rating: 5
    },
    {
      quote: "平台的易用性和专业技术支持让我们的团队能够快速上手。工作流引擎大大简化了我们的计算任务管理。",
      author: "张博士",
      position: "制药公司 研发总监",
      company: "某制药企业",
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + testimonials.length) % testimonials.length);
  };

  const goToTestimonial = (index: number) => {
    setCurrentIndex(index);
  };

  return (
    <section className="spacing-responsive bg-gradient-to-br from-gray-50 to-white">
      <div className="container-responsive">
        {/* Header */}
        <div className="text-center mb-16 portrait:mb-10">
          <h2 className="text-responsive-lg font-bold text-gray-900 mb-4 portrait:mb-3">
            用户评价
          </h2>
          <p className="text-responsive-sm text-gray-600 max-w-3xl mx-auto">
            听听我们的客户对ChainCloud云超算平台的评价
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl portrait:rounded-2xl shadow-2xl p-8 md:p-12 portrait:p-5 portrait:md:p-8 relative overflow-hidden">
            {/* Quote Icon */}
            <div className="absolute top-6 portrait:top-4 left-6 portrait:left-4 text-primary-200">
              <Quote size={48} className="portrait:w-8 portrait:h-8" />
            </div>

            {/* Main Testimonial */}
            <div className="relative z-10">
              {/* Quote */}
              <blockquote className="text-xl md:text-2xl portrait:text-base portrait:md:text-lg text-gray-700 leading-relaxed mb-8 portrait:mb-5 italic">
                "{testimonials[currentIndex].quote}"
              </blockquote>

              {/* Rating */}
              <div className="flex items-center gap-1 mb-6 portrait:mb-4">
                {[...Array(testimonials[currentIndex].rating)].map((_, index) => (
                  <div
                    key={index}
                    className="w-5 h-5 portrait:w-4 portrait:h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"
                  />
                ))}
              </div>

              {/* Author Info */}
              <div className="flex items-center gap-4 portrait:gap-3">
                <div className="w-16 h-16 portrait:w-12 portrait:h-12 bg-gradient-to-br from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white font-bold text-xl portrait:text-lg">
                  {testimonials[currentIndex].author.charAt(0)}
                </div>
                <div>
                  <div className="font-bold text-gray-900 text-lg portrait:text-base">
                    {testimonials[currentIndex].author}
                  </div>
                  <div className="text-gray-600 text-base portrait:text-sm">
                    {testimonials[currentIndex].position}
                  </div>
                  <div className="text-sm portrait:text-xs text-gray-500">
                    {testimonials[currentIndex].company}
                  </div>
                </div>
              </div>
            </div>

            {/* Background Pattern */}
            <div className="absolute top-0 right-0 w-64 h-64 portrait:w-32 portrait:h-32 bg-gradient-to-br from-primary-50 to-secondary-50 rounded-full -translate-y-32 portrait:-translate-y-16 translate-x-32 portrait:translate-x-16 opacity-50" />
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8 portrait:mt-5">
            {/* Previous Button */}
            <button
              onClick={prevTestimonial}
              className="group p-3 portrait:p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <ChevronLeft className="w-6 h-6 portrait:w-5 portrait:h-5 text-gray-600 group-hover:text-primary-600 transition-colors duration-300" />
            </button>

            {/* Dots Indicator */}
            <div className="flex items-center gap-3 portrait:gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToTestimonial(index)}
                  className={`w-3 h-3 portrait:w-2 portrait:h-2 rounded-full transition-all duration-300 ${
                    index === currentIndex
                      ? 'bg-gradient-to-r from-primary-500 to-secondary-500 scale-125'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>

            {/* Next Button */}
            <button
              onClick={nextTestimonial}
              className="group p-3 portrait:p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
            >
              <ChevronRight className="w-6 h-6 portrait:w-5 portrait:h-5 text-gray-600 group-hover:text-primary-600 transition-colors duration-300" />
            </button>
          </div>
        </div>

        {/* Bottom Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 portrait:grid-cols-1 gap-8 portrait:gap-5 mt-16 portrait:mt-10">
          <div className="text-center">
            <div className="text-4xl portrait:text-2xl font-bold text-primary-600 mb-2 portrait:mb-1">98%</div>
            <div className="text-gray-600 text-base portrait:text-sm">客户满意度</div>
          </div>
          <div className="text-center">
            <div className="text-4xl portrait:text-2xl font-bold text-secondary-600 mb-2 portrait:mb-1">24/7</div>
            <div className="text-gray-600 text-base portrait:text-sm">技术支持</div>
          </div>
          <div className="text-center">
            <div className="text-4xl portrait:text-2xl font-bold text-green-600 mb-2 portrait:mb-1">500+</div>
            <div className="text-gray-600 text-base portrait:text-sm">企业客户</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials; 